import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Target, ClipboardCheck, Play } from 'lucide-react';
import { ThemeId, themeById, MODULES, loadProgress, themeProgressPct, fmt } from '../data/learning';
import { moduleMinutes } from '../data/duration';
import { ThemeGlyph } from '../components/ui/learnbits';
import { ThemeIllustration } from '../components/ui/illustrations';
import { Reveal, ProgressRing } from '../components/ui/motion';

export const ThemeDetail: React.FC = () => {
  const { themeId } = useParams<{ themeId: string }>();
  const navigate = useNavigate();
  const t = themeById((themeId as ThemeId) || 'ai');
  const store = loadProgress();
  const st = store[t.id];
  const pct = themeProgressPct(t.id);
  const mods = MODULES[t.id];
  const statusLabel = pct >= 100 ? '✓ Afgerond' : pct > 0 ? 'Bezig' : 'Nieuw';
  const nextIdx = Math.min(st.doneModules, t.mods - 1);

  return (
    <div className="shell py-12 sm:py-16">
      <Link to="/themas" className="inline-flex items-center gap-1.5 text-sm text-sub hover:text-ink mb-6">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> Terug naar thema’s
      </Link>

      {/* Headerkaart */}
      <div className="card mb-6 overflow-hidden anim-rise">
        <div className="flex flex-wrap items-center gap-6 p-6 sm:p-7">
          <div className="flex items-start gap-4 flex-1 min-w-[260px]">
            <ThemeGlyph theme={t} size={58} />
            <div>
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <h1 className="font-display font-bold text-ink" style={{ fontSize: 'clamp(28px,4vw,40px)', letterSpacing: '-0.02em' }}>{t.name}</h1>
                <span className="chip-muted">{statusLabel}</span>
              </div>
              <p className="text-sub max-w-readable mb-4">{t.long}</p>
              <button onClick={() => navigate(`/thema/${t.id}/les/${nextIdx}`)} className="btn text-white" style={{ background: t.accent }}>
                <Play className="w-4 h-4" strokeWidth={0} fill="#fff" /> {pct > 0 ? `Verder met module ${nextIdx + 1}` : 'Start de eerste module'} <ArrowRight className="w-4 h-4 cta-arrow" strokeWidth={1.75} />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-6 flex-none">
            <ProgressRing
              pct={pct} color={t.accent} size={104}
              label={<span className="text-center"><span className="block font-display font-bold text-ink" style={{ fontSize: 24, lineHeight: 1 }}>{pct}%</span><span className="block text-xs text-muted mt-0.5">{st.doneModules}/{t.mods}</span></span>}
            />
            <ThemeIllustration themeId={t.id} className="hidden md:block" style={{ width: 190, height: 122 }} />
          </div>
        </div>
      </div>

      {/* Twee pijlers: zelfscan + toets */}
      <div className="grid gap-[18px] mb-8" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))' }}>
        <Reveal delay={40}>
          <div className="card card-hover p-6 flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
              <span className="grid place-items-center rounded-tile" style={{ width: 44, height: 44, background: '#F6EFD9' }}>
                <Target style={{ color: '#C29A33' }} strokeWidth={1.75} className="w-5 h-5" />
              </span>
              <span className="chip-gold">± 1 of 5 min</span>
            </div>
            <h2 className="font-display font-semibold text-ink mb-1" style={{ fontSize: 21 }}>Zelfscan</h2>
            <p className="text-sub mb-4 flex-1">Schat jezelf in en zie je score per onderdeel in een spinnenweb. Kies een snelle scan (± 1 min) of de volledige scan (± 5 min).</p>
            <p className="text-sm text-sub mb-4">{st.sa != null ? <>Laatste inschatting: <strong className="text-ink">{fmt(st.sa)}</strong></> : 'Nog niet gemaakt'}</p>
            <button onClick={() => navigate(`/thema/${t.id}/zelfscan`)} className="btn-gold self-start">
              {st.sa != null ? 'Scan opnieuw' : 'Start de zelfscan'} <ArrowRight className="w-4 h-4 cta-arrow" strokeWidth={1.9} />
            </button>
          </div>
        </Reveal>

        <Reveal delay={110}>
          <div className="card card-hover p-6 flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
              <span className="grid place-items-center rounded-tile" style={{ width: 44, height: 44, background: '#EAEEFC' }}>
                <ClipboardCheck style={{ color: '#2440CF' }} strokeWidth={1.75} className="w-5 h-5" />
              </span>
              <span className="chip-blue">10 vragen</span>
            </div>
            <h2 className="font-display font-semibold text-ink mb-1" style={{ fontSize: 21 }}>Toets</h2>
            <p className="text-sub text-sm mb-4 flex-1">Test je kennis met 10 vragen. Haal je een 5,5 of hoger, dan is je toets geslaagd en verdien je direct je certificaat.</p>
            <p className="text-sm text-sub mb-4">{st.toets != null ? <>Laatste score: <strong className="text-ink">{fmt(st.toets)}</strong></> : 'Nog niet gemaakt'}</p>
            <button onClick={() => navigate(`/thema/${t.id}/toets`)} className="btn-primary self-start">
              {st.toets != null ? 'Toets opnieuw' : 'Start de toets'} <ArrowRight className="w-4 h-4 cta-arrow" strokeWidth={1.75} />
            </button>
          </div>
        </Reveal>
      </div>

      {/* Trainingsonderdelen */}
      <h2 className="font-display font-bold text-ink mb-5" style={{ fontSize: 'clamp(22px,3vw,27px)' }}>Trainingsonderdelen</h2>
      <div className="flex flex-col gap-2.5">
        {mods.map((m, i) => {
          const done = i < st.doneModules;
          const current = i === st.doneModules;
          return (
            <Reveal key={i} delay={Math.min(i * 45, 320)}>
              <Link to={`/thema/${t.id}/les/${i}`} className="card card-hover p-4 flex items-center gap-4" style={current ? { borderLeft: `3px solid ${t.accent}` } : undefined}>
                <span className="grid place-items-center rounded-full flex-none text-sm font-semibold"
                  style={{ width: 32, height: 32, background: done ? t.tint : current ? t.accent : '#EFEAE3', color: done ? t.accent : current ? '#fff' : '#9A938A' }}>
                  {done ? <Check className="w-4 h-4" strokeWidth={2.5} /> : i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-ink truncate text-lg">{m.title}</p>
                  <p className="text-sm text-muted">{m.sub} · ± {moduleMinutes(t.id, i)} min</p>
                </div>
                <span className="text-sm font-medium flex-none inline-flex items-center gap-1" style={{ color: done ? '#C29A33' : current ? t.accent : '#9A938A' }}>
                  {done ? 'Afgerond' : current ? 'Verder gaan' : 'Te doen'}
                  {!done && <ArrowRight className="w-4 h-4 cta-arrow" strokeWidth={1.75} />}
                </span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </div>
  );
};
